import type { ReactNode } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Navigate } from "src/router";
import { NotFound } from "src/pages/not-found";

type Props = {
  /**
   * The feature flag key to check. This should be a key from the LaunchDarkly flags object.
   */
  flagKey: string;
  /**
   * Optional redirect path. If provided and the flag is off, redirects to this path.
   * If not provided and the flag is off, shows a 404 page.
   */
  redirect?: string;
  /**
   * Children to render when the feature flag is enabled.
   */
  children: ReactNode;
};

/**
 * A route wrapper that conditionally renders children based on a feature flag.
 *
 * @example
 * // Show 404 if flag is off
 * <FeatureFlagRoute flagKey="appPrivacyFeatures">
 *   <DataMaskingTable />
 * </FeatureFlagRoute>
 *
 * @example
 * // Redirect if flag is off
 * <FeatureFlagRoute flagKey="appPrivacyFeatures" redirect="/governance">
 *   <DataMaskingTable />
 * </FeatureFlagRoute>
 */
export function FeatureFlagRoute({ flagKey, redirect, children }: Props) {
  const flags = useFlags();
  const flagValue = flags[flagKey as keyof typeof flags];

  if (!flagValue) {
    if (redirect) {
      return <Navigate to={redirect} replace />;
    }
    return <NotFound />;
  }

  return <>{children}</>;
}
